import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { errMsg } from '@/services/staffAttendanceApi';
import { ewbApi } from '@/services/ewayBillApi';

const BLANK = { name: '', gstin: '', legal_name: '', address1: '', address2: '', place: '', pincode: '', state: 'Tamil Nadu', is_default: false };
const FIELDS = [['name', 'Unit name'], ['gstin', 'GSTIN'], ['legal_name', 'Legal name'], ['address1', 'Address line 1'],
  ['address2', 'Address line 2'], ['place', 'Place'], ['pincode', 'PIN code']];

const UnitCard = ({ u, i, states, set, remove }) => (
  <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid={`ewb-unit-${i}`}>
    <div className="grid md:grid-cols-4 gap-2">
      {FIELDS.map(([k, l]) => (
        <Input key={k} value={u[k]} onChange={(e) => set(i, k, e.target.value)} placeholder={l} className={k === 'gstin' ? 'font-mono uppercase' : ''} data-testid={`ewb-unit-${i}-${k}`} />
      ))}
      <select value={u.state} onChange={(e) => set(i, 'state', e.target.value)} className="h-10 rounded-md border border-gray-300 bg-white px-2 text-sm" data-testid={`ewb-unit-${i}-state`}>
        {states.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
    <div className="flex items-center justify-between text-sm">
      <label className="flex items-center gap-2"><input type="radio" checked={!!u.is_default} onChange={() => set(i, 'is_default', true)} data-testid={`ewb-unit-${i}-default`} />Default sender</label>
      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(i)} data-testid={`ewb-unit-${i}-remove`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  </div>
);

/** Admin: this site's sender units (GSTIN + address) — used when the ERP document has no consignor, and as receiving units for branch transfers. */
export const SenderUnitsDialog = ({ open, onClose, cfg, onSaved }) => {
  const [units, setUnits] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setUnits((cfg?.units || []).map((u) => ({ ...BLANK, ...u }))); }, [open, cfg]);
  const set = (i, k, v) => setUnits(units.map((u, n) => (n === i ? { ...u, [k]: v } : k === 'is_default' ? { ...u, is_default: false } : u)));
  const save = () => {
    setBusy(true);
    ewbApi.saveUnits(units).then((d) => { toast.success('Sender units saved'); onSaved(d.units); })
      .catch((e) => toast.error(errMsg(e, 'Save failed'), { duration: 8000 })).finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="ewb-units-dialog">
        <DialogHeader><DialogTitle>Sender units (this site)</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Used as the <b>From</b> address when the ERP document doesn't carry one, and as the receiving unit for branch transfers.</p>
        <div className="space-y-3">
          {units.map((u, i) => <UnitCard key={u.id || u._k} u={u} i={i} states={cfg?.states || []} set={set} remove={(n) => setUnits(units.filter((_, x) => x !== n))} />)}
          {!units.length && <p className="py-6 text-center text-sm text-gray-500" data-testid="ewb-units-empty">No units yet</p>}
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setUnits([...units, { ...BLANK, _k: `new-${Date.now()}`, is_default: !units.length }])} data-testid="ewb-unit-add"><Plus className="w-4 h-4 mr-1" />Add unit</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="ewb-units-save">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SenderUnitsDialog;
